import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  useHtmlToReact,
  type ElementTransform,
} from '@/components/Common/HtmlToReact';
import TriangleDownIcon from '@/public/icons/approval/triangle-down.svg';
import TriangleRightIcon from '@/public/icons/approval/triangle-right.svg';
import AttachmentImgIcon from '@/public/icons/approval/attachment-img.svg';
import { formatGroupSalesNumber } from '@/utils/approval/groupSales';
import {
  findGroupSalesMultiDetail,
  GROUP_SALES_MULTI_ERROR_CLASS,
  groupSalesMultiAmounts,
  groupSalesMultiAmountQuantity,
  groupSalesMultiDetailError,
  type GroupSalesMultiDetail,
  type GroupSalesMultiGroup,
} from '@/utils/approval/groupSalesMulti';
import { openDownloadUrlInNewTab } from '@/utils/approval/resolveDownloadUrl';
import { ParticipantTable, SaleItemTable } from './GroupSalesExpand';
import { GroupSalesRoot } from './styles';

const TABLE_CLASS = 'table text-center';

type TablesProps = {
  group: GroupSalesMultiGroup;
  details: GroupSalesMultiDetail[];
  t: TFunction;
};

/**
 * 레거시 appr_sub_view_forms_groupsales_multi 와 같은 class·id(btn-view-client-history, client-history-{매장}-{건}, btn-view[data-file]).
 * 조회 화면이 레거시 문서와 같은 규칙으로 펼침·여권 버튼을 붙임
 */
const GroupSalesMultiTables = ({ group, details, t }: TablesProps) => {
  const num = formatGroupSalesNumber;
  const isAmount = group.type === '금액';
  return (
    <div className="group-sales-multi">
      {group.shops.map((shop, s) => (
        <div key={shop.shopcode} className="group-sales-multi-shop">
          <h4>{`${group.name_eng} ${shop.shortname}`}</h4>
          <div>
            <table className={TABLE_CLASS}>
              <colgroup>
                <col width="40" />
                <col width="200" />
                <col />
                <col width="100" />
                {isAmount && <col width="90" />}
                <col width="120" />
                <col width="100" />
                <col width="130" />
                {!isAmount && <col width="90" />}
              </colgroup>
              <thead>
                <tr>
                  <th />
                  <th>{t('날짜')}</th>
                  <th>{isAmount ? t('납품처') : t('고객명')}</th>
                  <th>{isAmount ? t('수량') : t('인원수')}</th>
                  {isAmount && <th>{t('총 스타일')}</th>}
                  <th>{t('TAG가')}</th>
                  <th>{t('할인율')}</th>
                  <th>{t('실판매가')}</th>
                  {!isAmount && <th>{t('여권')}</th>}
                </tr>
              </thead>
              <tbody>
                {shop.items.map((item, i) => {
                  const detail = findGroupSalesMultiDetail(
                    details,
                    shop.shopcode,
                    item.seq,
                  );
                  const amounts = groupSalesMultiAmounts(detail, group.type);
                  const account = detail?.account;
                  const period = detail?.periodSummary;
                  const rowId = `client-history-${s}-${i}`;
                  const count = isAmount
                    ? groupSalesMultiAmountQuantity(detail)
                    : period?.count;
                  const participants = detail?.participants ?? [];
                  const passportCell = !isAmount && (
                    <td
                      className="group-sales-passport"
                      data-shop={s}
                      data-item={i}
                    >
                      {(item.passport || item.passport_no) && (
                        <a
                          className="btn-view"
                          data-file={item.passport}
                          data-uuid={item.passport_no}
                        />
                      )}
                    </td>
                  );
                  const error = groupSalesMultiDetailError(detail);
                  if (error !== null)
                    return (
                      <tr key={rowId} data-shop={s} data-item={i}>
                        <td />
                        <td>{item.date || t('판매기간 없음')}</td>
                        <td
                          colSpan={isAmount ? 6 : 5}
                          className={GROUP_SALES_MULTI_ERROR_CLASS}
                        >
                          {t('데이터를 불러오지 못했습니다.')}
                          {error && ` (${error})`}
                        </td>
                        {passportCell}
                      </tr>
                    );
                  return [
                    <tr key={rowId} data-shop={s} data-item={i}>
                      <td>
                        <a
                          className="btn-view-client-history"
                          data-target={`#${rowId}`}
                        />
                      </td>
                      <td>{item.date || t('판매기간 없음')}</td>
                      <td>
                        <span
                          title={
                            isAmount
                              ? t('담당자 : {{name}}({{contact}})', {
                                  name: account?.clientName ?? '',
                                  contact: account?.contact ?? '',
                                })
                              : t('사업자번호 : {{value}}', {
                                  value: period?.businessNumber ?? '',
                                })
                          }
                        >
                          {account?.balanceTypeName && (
                            <small className="d-block">
                              {`(${account.balanceTypeName})`}
                            </small>
                          )}
                          {account?.organizationName ??
                            period?.organizationName ??
                            ''}
                        </span>
                      </td>
                      <td>{count || '0'}</td>
                      {isAmount && <td>{detail?.styleCount ?? ''}</td>}
                      <td>{num(amounts.amount)}</td>
                      <td>
                        <span
                          title={t('할인금액 : {{amount}}', {
                            amount: num(amounts.discountAmount),
                          })}
                        >
                          {`${amounts.discountRate ?? ''}%`}
                        </span>
                      </td>
                      <td>{num(amounts.saleAmount)}</td>
                      {passportCell}
                    </tr>,
                    <tr key={`${rowId}-detail`} id={rowId} className="collapse">
                      <td colSpan={8}>
                        {isAmount && (detail?.saleItems ?? []).length > 0 && (
                          <SaleItemTable
                            summary={{
                              saleItems: detail?.saleItems,
                              styleCount: detail?.styleCount ?? null,
                            }}
                            t={t}
                          />
                        )}
                        {participants.length > 0 && (
                          <>
                            <h6>
                              {isAmount ? t('담당자 내역') : t('대상자 내역')}
                            </h6>
                            <ParticipantTable
                              summary={{ participants }}
                              t={t}
                              isAmount={isAmount}
                            />
                          </>
                        )}
                      </td>
                    </tr>,
                  ];
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

/** attributes.content — 작성 시점 내역(펼침 내역 포함) */
export const buildGroupSalesMultiContentHtml = (
  group: GroupSalesMultiGroup,
  details: GroupSalesMultiDetail[],
  t: TFunction,
): string =>
  renderToStaticMarkup(
    <GroupSalesMultiTables group={group} details={details} t={t} />,
  );

type ViewContextValue = {
  openRows: Set<string>;
  toggleRow: (id: string) => void;
  renderPassport?: (shopIndex: number, itemIndex: number) => ReactNode;
};

const ViewContext = createContext<ViewContextValue>({
  openRows: new Set(),
  toggleRow: () => undefined,
});

const RowToggle = ({ rowId }: { rowId: string }) => {
  const { t } = useTranslation();
  const { openRows, toggleRow } = useContext(ViewContext);
  const open = openRows.has(rowId);
  return (
    <RowToggleButton
      size="small"
      aria-expanded={open}
      aria-label={open ? t('닫기') : t('펼치기')}
      title={open ? t('닫기') : t('펼치기')}
      onClick={() => toggleRow(rowId)}
    >
      {open ? <TriangleDownIcon /> : <TriangleRightIcon />}
    </RowToggleButton>
  );
};

const DetailRow = ({
  rowId,
  children,
}: {
  rowId: string;
  children: ReactNode;
}) => {
  const { openRows } = useContext(ViewContext);
  if (!openRows.has(rowId)) return null;
  return <tr className="group-sales-multi-detail">{children}</tr>;
};

export const GroupSalesMultiPassportView = ({
  url,
  uuid,
}: {
  url: string;
  uuid?: string;
}) => {
  const { t } = useTranslation();
  return (
    <PassportViewButton
      size="small"
      aria-label={t('여권 보기')}
      title={t('여권 보기')}
      onClick={() => openDownloadUrlInNewTab(uuid || null, url)}
    >
      <AttachmentImgIcon />
    </PassportViewButton>
  );
};

const PassportCell = ({
  shopIndex,
  itemIndex,
  children,
}: {
  shopIndex: number;
  itemIndex: number;
  children: ReactNode;
}) => {
  const { renderPassport } = useContext(ViewContext);
  return (
    <td>{renderPassport ? renderPassport(shopIndex, itemIndex) : children}</td>
  );
};

const DROP_TAGS = new Set(['style', 'script', 'link', 'input', 'label']);
const TABLE_PARTS = new Set(['table', 'colgroup', 'thead', 'tbody', 'tr']);
const isBlankText = (node: ReactNode) =>
  typeof node === 'string' && !node.trim();
const hasClass = (element: Element, name: string) =>
  element.classList.contains(name);

/**
 * 저장 content(V2·레거시 공통) — 행 펼침은 닫힌 채 시작, 여권은 새 창으로 열기.
 * renderPassport 를 주면 여권 칸을 그 내용으로 바꿈(작성·편집)
 */
export const GroupSalesMultiSnapshot = ({
  content,
  renderPassport,
}: {
  content: string;
  renderPassport?: (shopIndex: number, itemIndex: number) => ReactNode;
}) => {
  const [openRows, setOpenRows] = useState<Set<string>>(() => new Set());
  const toggleRow = useCallback((id: string) => {
    setOpenRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const transform = useCallback<ElementTransform>(
    ({ element, tagName, props, children }) => {
      if (DROP_TAGS.has(tagName)) return null;
      if (
        hasClass(element, 'modal') ||
        hasClass(element, 'btn-delete-gsPassport')
      )
        return null;
      if (TABLE_PARTS.has(tagName)) {
        const kept = children.filter((c) => !isBlankText(c));
        children.splice(0, children.length, ...kept);
      }
      if (hasClass(element, 'btn-view-client-history')) {
        const target = (element.getAttribute('data-target') ?? '').replace(
          /^#/,
          '',
        );
        return <RowToggle key={props.key as string} rowId={target} />;
      }
      if (tagName === 'tr' && element.id.startsWith('client-history-'))
        return (
          <DetailRow key={props.key as string} rowId={element.id}>
            {children}
          </DetailRow>
        );
      if (hasClass(element, 'btn-view')) {
        const url = element.getAttribute('data-file') ?? '';
        const uuid = element.getAttribute('data-uuid') ?? '';
        // 레거시는 주소만, V2 는 주소를 못 받았어도 uuid 로 열 수 있음
        if (!url && !uuid) return null;
        return (
          <GroupSalesMultiPassportView
            key={props.key as string}
            url={url}
            uuid={uuid}
          />
        );
      }
      if (hasClass(element, 'view-box')) delete props.style;
      if (hasClass(element, 'group-sales-passport'))
        return (
          <PassportCell
            key={props.key as string}
            shopIndex={Number(element.getAttribute('data-shop'))}
            itemIndex={Number(element.getAttribute('data-item'))}
          >
            {children}
          </PassportCell>
        );
      return undefined;
    },
    [],
  );
  const rendered = useHtmlToReact(content, { transform });
  return (
    <ViewContext.Provider value={{ openRows, toggleRow, renderPassport }}>
      <MultiRoot>{rendered}</MultiRoot>
    </ViewContext.Provider>
  );
};

const ToggleButton = styled(IconButton)({
  width: 32,
  height: 32,
  '& svg': { width: 20, height: 20 },
});

/** 첨부 이미지 아이콘은 그림 둘레 여백이 커서 크게 그림 */
const PassportViewButton = styled(IconButton)({
  width: 32,
  height: 32,
  padding: 0,
  '& svg': { width: 28, height: 28 },
});

/** 레거시 첫 칸 40px = 칸 안쪽 여백 좌우 6px + 버튼 28px */
const RowToggleButton = styled(ToggleButton)({
  width: 28,
  height: 28,
  '& svg': { width: 12, height: 12 },
});

const MultiRoot = styled(GroupSalesRoot)(({ theme }) => ({
  '& table': { minWidth: 960 },
  '& td table': { minWidth: 0 },
  '& th, & td table td': { wordBreak: 'keep-all' },
  '& h4': {
    margin: '16px 0 8px',
    fontSize: 15,
    fontWeight: 600,
  },
  '& .group-sales-multi-shop:first-of-type h4, & #apv_groupsales_multi_detail > h4:first-of-type':
    { marginTop: 0 },
  '& h6': {
    margin: '8px 0',
    fontSize: 13,
    fontWeight: 500,
    textAlign: 'left',
    color: theme.palette.text.secondary,
  },
  '& small.d-block': {
    display: 'block',
    fontSize: 11,
    color: theme.palette.text.secondary,
  },
  [`& td.${GROUP_SALES_MULTI_ERROR_CLASS}`]: {
    color: theme.palette.error.main,
  },
  '& tr.group-sales-multi-detail > td': {
    padding: 8,
    backgroundColor: 'var(--dk-bg-subtle, #f9fafb)',
  },
}));
